import React, { useCallback, useContext, useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';

import Hours from '../components/Hours';
import Layout from '../components/Layout';
import LocationHeader from '../components/LocationHeader';
import Meal from '../components/Meal';
import MealPicker from '../components/MealPicker';
import MenuLocationMap from '../components/MenuLocationMap';
import common from '../utility/common.js';
import { getDateForParams } from '../utility/index.js';
import moment from 'moment';
import strings from '../utility/strings.js';
import {useAppState} from '../../index.js';

const GET_LOCATION_MENU = gql`
  query getLocationMenu ($name: String!, $date: Date!) {
    diningCourtByName(name: $name) {
      address {
				city
				state
				street
				zip
			}
			formalName
			id
      bannerUrl
			logoUrl
      name
			latitude
      longitude
      googlePlaceId
      normalHours {
				id
				name
				effectiveDate
				days {
					dayOfWeek
					meals {
						endTime
						name
						startTime
					}
				}
			}
      dailyMenu (
				date: $date
			) {
        notes
				meals {
					endTime
					startTime
					notes
					name
					status
					stations {
						iconUrl
						id
						name
						notes
						items {
							specialName
              itemMenuId
              hasComponents
							item {
								isFlaggedForCurrentUser
								isHiddenForCurrentUser
								isNutritionReady
								itemId
								name
								traits {
									name
									svgIcon
									svgIconWithoutBackground
								}
								components {
									name
                  isFlaggedForCurrentUser
                  isHiddenForCurrentUser
                  isNutritionReady
                  itemId
									traits {
										name
										svgIcon
										svgIconWithoutBackground
									}
								}
							}
						}
					}
				}
			}
    }
  }
`;

const Location = (props) => {
  const {locations, currentUser, GET_USER} = useAppState();
  const { AppContext } = props;
  const { locationName, year, month, day, meal } = useParams();
  const [dailyMenu, setDailyMenu] = useState([]);
  const [selectedMealName, setSelectedMealName] = useState('');
  const [locationDetails, setLocationDetails] = useState();
  const [status, setStatus] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if(year === undefined || month === undefined || day === undefined) {
      let todayDate = moment().format("YYYY") + "/" + moment().format("M") + "/" + moment().format("DD");
      navigate(`${todayDate}/`);
    }
  }, [year, month, day, navigate]);

  let params = { locationName: locationName, year: year, month: month, day: day, meal: meal };
  let date;
  let dateFull;
  if ((year === undefined || month === undefined || day === undefined)) {
    date = moment().format("YYYY") + "-" + moment().format("M") + "-" + moment().format("DD");
    dateFull = moment().format("MMM") + " " + moment().format("D") + ", " + moment().format("YYYY");
  } else {
    date = getDateForParams(params).format('YYYY-MM-DD')
    dateFull = getDateForParams(params).format('MMM D, YYYY')
  }

  const locationMenuQuery = useQuery(GET_LOCATION_MENU, {
    variables: { name: locationName, date: date },
    errorPolicy: 'all' // Allows data to be returned even with field errors
  });

  //console.log('LocationMenuQuery loading:',locationName, locationMenuQuery.loading, 'data:', locationMenuQuery.data);

  function selectMeal(meal, e) {
    let mealName = typeof meal === "string" ? meal : meal.name;
    if (e && e.preventDefault) { e.preventDefault(); }
    setSelectedMealName(mealName);
  }

  const getMealForParams = useCallback((varMeals) => {
    let currentMeal = null;
    let firstMeal = null;
    let elementPos = varMeals.map(function (x) { return x.name; }).indexOf(meal);
    if (varMeals[0]) {
      firstMeal = varMeals[0].name;
    }
    if (meal && elementPos === -1) {
      return varMeals[0].name;
    } else if (meal && elementPos !== -1) {
      return meal;
    }
    else {
      for(const x of varMeals) {
        if (x.startTime && x.endTime && x.status !== "Unavailable") {
          let StartTime = moment.parseZone(x.startTime);
          let EndTime = moment.parseZone(x.endTime);
          let Now = moment();

          if (x.status === 'OPEN' && StartTime !== null && EndTime !== null && Now.isBetween(StartTime, EndTime, undefined, '[]')) {
            currentMeal = x.name;
            break;
          } else if (x.status === 'OPEN' && StartTime !== null && EndTime !== null && moment().isBefore(StartTime)) {
            currentMeal = x.name;
            break;
          }
        }
      }
      if (currentMeal === null) {
        return firstMeal;
      }
      else {
        return currentMeal;
      }
    }
  }, [meal]);

  const loadLocation = useCallback((loading, data) => {
    if (!loading && data.diningCourtByName.id !== undefined) {
      let locationDetails = data.diningCourtByName;
      setLocationDetails({
        address: locationDetails.address,
        formalName: locationDetails.formalName,
        id: locationDetails.id,
        latitude: locationDetails.latitude,
        longitude: locationDetails.longitude,
        logoUrl: locationDetails.logoUrl,
        normalHours: locationDetails.normalHours,
        bannerUrl: locationDetails.bannerUrl,
        googlePlaceId: locationDetails.googlePlaceId,
        name: locationDetails.name
      });
    }
  }, []);

  const loadMenu = useCallback((loading, data) => {
    //console.log('loading error, current state:', data);
    if (!loading && data && data.diningCourtByName.dailyMenu !== undefined) {
      let dailyMenuMeals = data.diningCourtByName.dailyMenu.meals;

      if (dailyMenuMeals.length === 0) {
        setStatus('Hidden');
      } else {
        let paramMeal = getMealForParams(dailyMenuMeals);
        let mealIndex = dailyMenuMeals.map((x) => { return x.name }).indexOf(paramMeal);
        setStatus(dailyMenuMeals[mealIndex].status);
        setDailyMenu(dailyMenuMeals);
        setSelectedMealName(paramMeal);
      }
    }
  }, [getMealForParams]);

  useEffect(() => {
    loadMenu(locationMenuQuery.loading, locationMenuQuery.data);
    loadLocation(locationMenuQuery.loading, locationMenuQuery.data);
  }, [locationMenuQuery, loadLocation, loadMenu]);

  if (
    dailyMenu
  ) {
    const locationTitle = locationDetails ? `${locationDetails.formalName} - Menu for ${dateFull}` : `${locationName} - Dining Menu`;
    const locationDescription = locationDetails ? 
      `View today's menu, hours, and dining options at ${locationDetails.formalName}. Browse meals, nutrition facts, and dietary information.` :
      `View the dining menu and hours for ${locationName} at Purdue University.`;
      
    return <>
      <Helmet>
        <title>{locationTitle} - Purdue Dining</title>
        <meta name="description" content={locationDescription} />
        <meta name="keywords" content={`${locationName}, Purdue dining menu, campus dining, meal times, nutrition facts`} />
        <meta property="og:title" content={locationTitle} />
        <meta property="og:description" content={locationDescription} />
      </Helmet>
      <Layout
      {...locationMenuQuery.data}
      params={{ locationName: locationName, year: year, month: month, day: day, meal: meal }}
      AppContext={AppContext}
      // https://github.itap.purdue.edu/IT-Enterprise-Solutions/menus-website/blob/master/src/js/pages/Location.jsx#L158
      menuLink={`${common.baseUrl}v2/pdf/${locationName}/${date}`}
      pageName={locationDetails ? locationDetails.formalName : " "}
      locations={locations} currentUser={currentUser} GET_USER={GET_USER}
    >
      <LocationRender
        menuNotes={!locationMenuQuery.loading && locationMenuQuery.data ? locationMenuQuery.data.diningCourtByName.dailyMenu.notes : ''}
        loadingMenu={locationMenuQuery.loading}
        loadingLocation={locationMenuQuery.loading}
        selectMeal={selectMeal}
        getMealForParams={getMealForParams}
        loadMenu={loadMenu}
        dailyMenu={dailyMenu}
        date={date}
        locationDetails={locationDetails}
        params={params}
        dateFull={dateFull}
        selectedMealName={selectedMealName}
        status={status}
        loading={locationMenuQuery.loading}
        AppContext={AppContext}
      />
    </Layout>
    </>
  } else {
    return <Loading />
  }
}

const Loading = () => {
  const {locations, currentUser, GET_USER} = useAppState();
  return <Layout locations={locations} currentUser={currentUser} GET_USER={GET_USER} pageName="Loading"><main className="mdl-layout__content" style={{ minHeight: "300px" }}>
    <div className={'loading'} style={{ paddingTop: "100px", background: "#ffffff" }}>
      <div className="circle">
        <div className="circle">
          <div className="circle">
            <div className="circle-center">
              <div className="circle-center__inner"></div>
            </div>
          </div>
        </div>
      </div>
      <div className="loading-text" style={{ color: "#444" }}>{"Loading Location"}</div>
    </div>
  </main>
  </Layout>
}

const LocationRender = (props) => {
  const { loading, selectMeal, dailyMenu, locationDetails, params, selectedMealName, AppContext, menuNotes } = props;
  const { styleprops, currentUser, highlightString, showOnlyString, hiddenString } = useContext(AppContext);

  function getNotes() {
    if (selectedMealName) {
      const selectedMeal = dailyMenu.find(x => x.name === selectedMealName);
      if (selectedMeal.notes && selectedMeal.notes.length > 0) {
        return selectedMeal.notes;
      }
    }
    return '';
  }

  let fetchedNotes = getNotes();
  let notesElement = fetchedNotes.length > 0 ? <div key={"locationNotes_" + selectedMealName} className="location-notes">{fetchedNotes}</div> : null;

  return (
    <main className={"mdl-layout__content"} style={styleprops} id="main">
      <div id="location-tab-empty"></div>

      {currentUser && locationDetails ?
        <div className="mdl-layout__content-page" id="main">
          <LocationHeader key="location-header"
            selectedDate={getDateForParams(params)}
            params={params}
            imageUrl={locationDetails.bannerUrl}
            locationName={locationDetails.name}
            menuNotes={menuNotes}>

            {selectedMealName ?
              <MealPicker key="mealpicker"
                locationName={locationDetails.name}
                dailyMenu={dailyMenu}
                selectedMealName={selectedMealName}
                selectedDate={getDateForParams(params)}
                selectMeal={selectMeal}
              />
              :
              null
            }

          </LocationHeader>
          {notesElement}
          <Meal {...dailyMenu.find((x) => { return x.name === selectedMealName; })}
            {...props}
            params={params}
            highlightString={highlightString}
            showOnlyString={showOnlyString}
            hiddenString={hiddenString}
            currentUser={currentUser}
            key="meal"
          />
          <div key="disclaimer" className="menu-disclaimer--location"><p>{strings.disclaimer}</p></div>
          <div key="widgets" className="location-widgets-container">
            <h2><span>About {locationDetails.name ? locationDetails.name : ""}</span></h2>
            <div className="location-widgets">
              <Hours key="hours" {...props} />
              <MenuLocationMap loading={loading} {...props} />
            </div>
          </div>
        </div>

        : <div className="mdl-layout__content-page" id="main"></div>}
    </main>
  )
}

export default Location;